import { useEffect, useState } from 'react';

import {
  downloadAnalyticsPdf,
  downloadAnalyticsReport,
  getAnalyticsConfig,
  getAnalyticsDashboard,
} from '../api/client.js';
import AnalyticsLayout from '../components/AnalyticsLayout.jsx';
import AnalyticsPie from '../components/AnalyticsPie.jsx';
import HourlyTrafficChart from '../components/HourlyTrafficChart.jsx';

const SCOPES = [
  { key: 'site', label: 'Whole Site' },
  { key: 'hub', label: 'Upgrade Page' },
  { key: 'upgrade', label: 'All Upgrade Pages' },
  { key: 'library', label: 'Resource Library' },
];

const SUPPORT_TILES = [
  { key: 'signin', icon: '🔐', label: 'Digital Banking Sign-in' },
  { key: 'contact', icon: '📄', label: 'Contact Us Page' },
  { key: 'phone', icon: '📞', label: 'Phone Clicks' },
  { key: 'email', icon: '✉️', label: 'Email Clicks' },
  { key: 'quiz', icon: '📝', label: 'Quiz Starts' },
  { key: 'app', icon: '📱', label: 'App Downloads' },
];

const iso = (d) => d.toISOString().slice(0, 10);

function presetRange(preset, config) {
  const today = new Date();
  const end = iso(today);
  if (preset === 'weekend' && config?.upgrade_weekend_start) {
    return { start: config.upgrade_weekend_start, end: config.upgrade_weekend_end };
  }
  if (preset === 'today') return { start: end, end };
  if (preset === '7d') {
    const d = new Date(today); d.setDate(d.getDate() - 6); return { start: iso(d), end };
  }
  if (preset === '30d') {
    const d = new Date(today); d.setDate(d.getDate() - 29); return { start: iso(d), end };
  }
  return { start: '', end: '' };
}

function fmtNum(n) {
  return typeof n === 'number' ? n.toLocaleString() : '—';
}

function fmtSeconds(s) {
  const n = Math.round(s || 0);
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
}

function weekendStatus(config) {
  if (!config?.upgrade_weekend_start) return null;
  const today = iso(new Date());
  if (today < config.upgrade_weekend_start) {
    const days = Math.ceil(
      (new Date(config.upgrade_weekend_start) - new Date(today)) / 86400000,
    );
    return { text: `T-minus ${days} day${days === 1 ? '' : 's'}`, tone: 'soon' };
  }
  if (today <= config.upgrade_weekend_end) return { text: 'LIVE NOW', tone: 'live' };
  return { text: 'Complete', tone: 'done' };
}

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="uw-delta uw-delta--new">new</span>;
  const up = v >= 0;
  return (
    <span className={`uw-delta ${up ? 'uw-delta--up' : 'uw-delta--down'}`}>
      {up ? '▲' : '▼'} {Math.abs(v)}%
    </span>
  );
}

function StatTile({ label, value, delta, priorLabel }) {
  return (
    <div className="uw-stat">
      <div className="uw-stat__label">{label}</div>
      <div className="uw-stat__value">{value}</div>
      {delta !== undefined && (
        <div className="uw-stat__delta">
          <Delta v={delta} /> <span className="uw-stat__prior">{priorLabel}</span>
        </div>
      )}
    </div>
  );
}

function RankList({ rows, nameKey, valueKey, empty, fmt = fmtNum }) {
  if (!rows || rows.length === 0) return <div className="chart-card__empty">{empty}</div>;
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  return (
    <div className="analytics-bars">
      {rows.slice(0, 12).map((r, i) => (
        <div className="analytics-bar" key={`${r[nameKey]}-${i}`}>
          <span className="analytics-bar__label" title={r[nameKey]}>{r[nameKey] || '(none)'}</span>
          <span className="analytics-bar__track">
            <span className="analytics-bar__fill" style={{ width: `${Math.max((r[valueKey] / max) * 100, 2)}%` }} />
          </span>
          <span className="analytics-bar__value">{fmt(r[valueKey])}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ eyebrow, title, hint, children }) {
  return (
    <section className="uw-section">
      <div className="uw-section__head">
        <div>
          <div className="uw-section__eyebrow">{eyebrow}</div>
          <h2 className="uw-section__title">{title}</h2>
        </div>
        {hint && <div className="uw-section__hint">{hint}</div>}
      </div>
      {children}
    </section>
  );
}

export default function UpgradeAnalyticsPage() {
  const [config, setConfig] = useState(null);
  const [range, setRange] = useState({ start: '', end: '' });
  const [scope, setScope] = useState('upgrade');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAnalyticsConfig()
      .then((c) => { setConfig(c); setRange(presetRange('weekend', c)); })
      .catch((e) => setError(e.message || 'Failed to load analytics config'));
  }, []);

  useEffect(() => {
    if (!range.start || !range.end) return;
    let cancelled = false;
    setLoading(true);
    getAnalyticsDashboard({ start_date: range.start, end_date: range.end, scope })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.start, range.end, scope]);

  const ov = data?.overview;
  const d = ov?.delta_pct || {};
  const notConnected = config && !config.configured;
  const status = weekendStatus(config);
  const reportParams = { start_date: range.start, end_date: range.end, scope };

  return (
    <AnalyticsLayout>
      {/* Branded command-center header */}
      <div className="uw-hero">
        <div className="uw-hero__main">
          <div className="uw-hero__eyebrow">EVOLUTION · WEB COMMAND CENTER</div>
          <h1 className="uw-hero__title">Upgrade Related Website Analytics</h1>
        </div>
        {status && <div className={`uw-status uw-status--${status.tone}`}>{status.text}</div>}
      </div>

      {/* Controls: page-scope buttons, date presets, exports */}
      <div className="uw-controls">
        <div className="uw-scope">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`uw-scope__btn${scope === s.key ? ' uw-scope__btn--active' : ''}`}
              onClick={() => setScope(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="uw-controls__right">
          <div className="report-card__presets">
            <button type="button" className="report-card__preset" onClick={() => setRange(presetRange('weekend', config))}>Upgrade Weekend</button>
            <button type="button" className="report-card__preset" onClick={() => setRange(presetRange('today', config))}>Today</button>
            <button type="button" className="report-card__preset" onClick={() => setRange(presetRange('7d', config))}>Last 7 Days</button>
            <button type="button" className="report-card__preset" onClick={() => setRange(presetRange('30d', config))}>Last 30 Days</button>
          </div>
          <div className="uw-exports">
            <button type="button" className="btn" onClick={() => downloadAnalyticsPdf(reportParams)}>Export PDF</button>
            <button type="button" className="btn" onClick={() => downloadAnalyticsReport(reportParams)}>Export CSV</button>
          </div>
        </div>
      </div>

      {/* Custom date range */}
      <div className="uw-daterange">
        <label className="report-card__field">
          <span>Start</span>
          <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
        </label>
        <label className="report-card__field">
          <span>End</span>
          <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
        </label>
      </div>

      <div className="uw-range-note">
        Showing <strong>{SCOPES.find((s) => s.key === scope)?.label}</strong> · {range.start} → {range.end}
      </div>

      {error && <div className="migration-banner">{error}</div>}

      {notConnected && (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <h3 style={{ marginTop: 0 }}>Google Analytics not connected</h3>
          <p style={{ color: 'var(--color-text-muted)' }}>
            Set <code>GA_PROPERTY_ID</code> and a service-account key on the server to enable this dashboard.
          </p>
        </div>
      )}

      {!notConnected && (
        <>
          {/* 1 — ADOPTION */}
          <Section eyebrow="Adoption" title="Are clients using the site?" hint="vs. prior equal period">
            <div className="uw-stats">
              <StatTile label="Users" value={fmtNum(ov?.total_users)} delta={d.total_users} priorLabel={`was ${fmtNum(ov?.prior?.total_users)}`} />
              <StatTile label="Sessions" value={fmtNum(ov?.sessions)} delta={d.sessions} priorLabel={`was ${fmtNum(ov?.prior?.sessions)}`} />
              <StatTile label="Pageviews" value={fmtNum(ov?.pageviews)} delta={d.pageviews} priorLabel={`was ${fmtNum(ov?.prior?.pageviews)}`} />
              <StatTile label="Engagement Rate" value={ov ? `${ov.engagement_rate}%` : '—'} />
              <StatTile label="Avg Session" value={ov ? fmtSeconds(ov.avg_session_seconds) : '—'} />
              <StatTile label="Engaged Sessions" value={fmtNum(ov?.engaged_sessions)} />
            </div>
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card__header"><strong>Hourly Traffic</strong></div>
              <div className="card__body"><HourlyTrafficChart rows={data?.hourly} loading={loading} /></div>
            </div>
          </Section>

          {/* 2 — WHAT THEY'RE SEEKING */}
          <Section eyebrow="What they're seeking" title="Which information are they after?" hint="content demand → what to create/adjust">
            <div className="chart-row">
              <div className="chart-card">
                <div className="chart-card__title">Top Site Searches</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList rows={data?.searches} nameKey="term" valueKey="count" empty="No searches in range" />
                </div>
              </div>
              <div className="chart-card">
                <div className="chart-card__title">Content &amp; FAQ Clicks</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList rows={data?.content_clicks} nameKey="text" valueKey="count" empty="No content clicks in range" />
                </div>
              </div>
              <div className="chart-card">
                <div className="chart-card__title">Top Pages</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList
                    rows={(data?.pages || []).map((p) => ({ name: p.title || p.path, views: p.views }))}
                    nameKey="name" valueKey="views" empty="No page views in range"
                  />
                </div>
              </div>
            </div>
          </Section>

          {/* 3 — WHERE THEY NEED SUPPORT */}
          <Section eyebrow="Where they need support" title="Where are clients reaching for help?" hint="support & conversion signals">
            <div className="uw-support">
              {SUPPORT_TILES.map((t) => (
                <div className="uw-support__tile" key={t.key}>
                  <span className="uw-support__icon">{t.icon}</span>
                  <span className="uw-support__val">{fmtNum(data?.support?.[t.key] ?? 0)}</span>
                  <span className="uw-support__label">{t.label}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* 4 — INTERNAL TRAFFIC TO THE UPGRADE PAGE */}
          <Section
            eyebrow="Internal traffic"
            title="Which centier.com pages send visitors to the upgrade page?"
            hint="internal referrers only — external search and direct arrivals excluded"
          >
            <div className="chart-row">
              <div className="chart-card">
                <div className="chart-card__title">Referring Pages → Upgrade Page</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList
                    rows={data?.upgrade_internal_referrers}
                    nameKey="page"
                    valueKey="views"
                    empty="No internal referrals in range"
                  />
                </div>
              </div>
            </div>
          </Section>

          {/* 5 — HOW THEY ARRIVE */}
          <Section eyebrow="How they arrive" title="Where is the traffic coming from?" hint="channels incl. email / QR / social">
            <div className="chart-row">
              <div className="chart-card">
                <div className="chart-card__title">Traffic by Channel</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList rows={data?.channels} nameKey="name" valueKey="sessions" empty="No channel data in range" />
                </div>
              </div>
              <AnalyticsPie title="Devices" items={(data?.devices || []).map((x) => ({ name: x.name, value: x.sessions }))} />
              <div className="chart-card">
                <div className="chart-card__title">Top Sources / Mediums</div>
                <div className="chart-card__body chart-card__body--list">
                  <RankList
                    rows={(data?.sources || []).map((s) => ({ name: `${s.source} / ${s.medium}`, sessions: s.sessions }))}
                    nameKey="name" valueKey="sessions" empty="No source data in range"
                  />
                </div>
              </div>
            </div>
          </Section>
        </>
      )}
    </AnalyticsLayout>
  );
}
